import { z } from 'zod'

import type {
  AdapterReferenceFilter,
  AdapterSchemaAttributes,
  AdapterSchemaRelation,
} from '../engine/contracts'
import { getHostRuntime } from '../engine/host-runtime'
import { getLocalizedTitle } from './shared/schemaLocalization'
import {
  EnumValueDefinition,
  expandEnumValuesWithSameTitle,
  isMultiValueOperator,
  normalizeOperatorForEnumValues,
} from '@/Atomic/Organisms/FilterBuilder/config'
import type {
  SavedFilterCondition,
  SavedFilterGroup,
  SavedFilterStructure,
} from '@/Atomic/Organisms/FilterBuilder/types'

/**
 * Filter-parity helpers shared by the chatbot's filter tools (build_filter,
 * save_filter, show_filter_preview). Isolated here so the manual FilterBuilder
 * coupling (`@/Atomic/Organisms/FilterBuilder/*`) lives in one clearly-named file
 * instead of leaking into the general-purpose entity-serializer.
 */

/**
 * Zod shape of a `SavedFilterStructure` as relayed between the chatbot filter tools
 * (build_filter → show_filter_preview → save_filter_to_segment). `value` stays
 * `unknown` — enum/reference codes are already resolved by build_filter, so no further
 * validation is needed here. Shared so the tools that accept a relayed structure parse
 * it identically. Accepts a JSON string or an object via the caller's `z.preprocess`.
 */
export const savedFilterStructureSchema = z.object({
  or: z
    .array(
      z.object({
        and: z
          .array(
            z.object({
              field: z.string(),
              op: z.string(),
              value: z.unknown(),
            }),
          )
          // A group with no conditions is degenerate — reject so canonicalization
          // and equality (find_matching_segment) never see an empty `and`.
          .min(1),
      }),
    )
    // A filter with no groups is not a filter — reject at parse for the same reason.
    .min(1),
})
// NOTE: this schema MUST stay transform-free. The filter tools embed it in their
// `inputSchema`, and the engine serializes every tool schema to JSON Schema
// (`toOpenAI`), which throws on any `.transform` ("Transforms cannot be represented
// in JSON Schema") — that would break the very first turn for ALL tools, not just
// filters. The `z.unknown()` value widens to an optional `value?: unknown` in the
// inferred type, which is structurally compatible with `SavedFilterStructure` at
// every current call site, so no cast is needed.

/**
 * Last structure `build_filter` produced, keyed by base entity type. `build_filter`
 * writes it; `create_segment` / `find_matching_segment` read it instead of trusting
 * the `structure` the model relays as a tool argument.
 *
 * Rationale (same failure `buildFilterLabelMaps` avoids for labels): a model asked to
 * copy a nested filter object between tool calls — especially ACROSS turns, e.g.
 * build_filter in one turn then create_segment after the user's "yes" — re-types it
 * from memory and corrupts it (a value array collapses to a scalar → "in requires a
 * slice value"; an enum code is hallucinated → "invalid enum value PSA_..."). The
 * canonical structure is derived once by build_filter and read back verbatim here, so
 * the create path never depends on the model reproducing it.
 */
const lastBuiltFilters = new Map<string, SavedFilterStructure>()

/** build_filter records its exact output here for the create/dedup tools to read back. */
export function cacheBuiltFilter(
  entityType: string,
  structure: SavedFilterStructure,
): void {
  lastBuiltFilters.set(entityType, structure)
}

/** The last build_filter structure for this entity, or undefined if none was built. */
export function getCachedFilter(
  entityType: string,
): SavedFilterStructure | undefined {
  return lastBuiltFilters.get(entityType)
}

/** Drops the cached filter once its create flow completes, so a later unrelated
 * create can never read a stale structure. */
export function clearCachedFilter(entityType: string): void {
  lastBuiltFilters.delete(entityType)
}

/**
 * Normalizes an enum/reference filter condition the way the manual FilterBuilder
 * does — the bot emits either a display label or a PISA code, this makes its
 * output byte-identical to a manually-built filter. Reuses the manual builder's
 * canonical primitives (`normalizeOperatorForEnumValues` +
 * `expandEnumValuesWithSameTitle`) so there is one source of truth for the
 * duplicate-title expansion. Bot-only: the manual builder calls those primitives
 * inline in useFilterBuilder.
 *
 *   1. Resolve each entry to its PISA code(s), case-insensitively (label → code).
 *   2. Flip `eq`→`in` / `neq`→`nin` when the enum has duplicate titles (one human
 *      label maps to several codes).
 *   3. Expand each code to ALL sibling codes sharing its title, so "status is
 *      Active" matches every code under that label — not just the first.
 *
 * Returns `{ op, value }`: scalar (or null) for single-value ops, array for
 * multi-value ops.
 */
// Minimal schema-attribute shape the filterability guards need. Both the
// Scrivito util schema (getSchemaAttributes) and the host-neutral adapter schema
// (AdapterSchemaAttributes) satisfy it, so the guard below is one source of truth
// across list_entities, count_entities, and build_filter.
interface FilterableFieldAttr {
  type: string
  readonly?: boolean
}

/**
 * The model-provided filter fields the backend cannot honour because they are
 * computed/readonly (e.g. Opportunity `open`, a readonly computed boolean). PISA
 * either silently ignores them on the flat channel (returning UNFILTERED rows the
 * model then narrates as matches) or 422s on the structured channel. Date types
 * are the one readonly exception — createdAt/updatedAt are readonly yet
 * filterable/sortable — so they are allowed. Schema-driven and host-neutral: any
 * connector whose schema marks computed fields readonly gets the same guard.
 * Returns the offending field names (empty when every field is filterable).
 */
export function findReadonlyFilterFields(
  fields: Iterable<string>,
  attrs: Record<string, FilterableFieldAttr | undefined>,
): string[] {
  const bad: string[] = []
  for (const field of fields) {
    const attr = attrs[field]
    if (attr?.readonly && attr.type !== 'date' && attr.type !== 'datetime') {
      bad.push(field)
    }
  }
  return bad
}

/**
 * The editable enum field a "state" filter (open/active/won) should target
 * instead of a computed field — prefers one literally named "status", else the
 * first editable enum. Used to steer the model off a rejected readonly field.
 */
export function pickEditableStatusField(
  attrs: Record<string, FilterableFieldAttr | undefined>,
): string | undefined {
  const status = attrs['status']
  if (status?.type === 'enum' && !status.readonly) {
    return 'status'
  }
  return Object.entries(attrs).find(
    ([, a]) => a?.type === 'enum' && !a?.readonly,
  )?.[0]
}

/** A structured filter condition (field + operator + value). */
export interface FlatCondition {
  field: string
  op: string
  value: unknown
}

/**
 * Expands structured `in`/`eq` conditions into the cartesian product of flat
 * equality filter sets — the fallback for an instance whose structured `?query=`
 * channel is unavailable (it 422s on every structured query, even a well-formed
 * one). A multi-value `in` (e.g. "open" = `status in [6 codes]`) cannot be
 * expressed on the flat `?field=value` channel in one request, but the UNION of
 * one flat query per value is equivalent. Each `in` condition contributes one
 * value per set; `eq` conditions are copied verbatim. Returns null when the
 * product would exceed `cap` (too many combinations to run as separate queries —
 * the caller should ask the user to narrow) or when a condition uses an operator
 * with no flat-equality equivalent (`nin`, ranges, relative dates), which the
 * flat channel cannot represent.
 */
export function expandConditionsToFlatSets(
  conditions: FlatCondition[],
  cap: number,
): Record<string, unknown>[] | null {
  let sets: Record<string, unknown>[] = [{}]
  for (const cond of conditions) {
    const values =
      cond.op === 'in'
        ? Array.isArray(cond.value)
          ? cond.value
          : [cond.value]
        : cond.op === 'eq'
          ? [cond.value]
          : null
    if (values === null) {
      return null
    }
    const next: Record<string, unknown>[] = []
    for (const base of sets) {
      for (const value of values) {
        next.push({ ...base, [cond.field]: value })
      }
    }
    if (next.length > cap) {
      return null
    }
    sets = next
  }
  return sets
}

// EnumValueDefinition extended with the PISA group metadata that lives in
// SchemaEnumData.group — present at runtime but not in the base FilterBuilder type.
// Both callers (list_entities + build_filter) pass SchemaEnumValues[] which has
// data?.group, so group expansion works automatically wherever schema data is available.
type EnumValueWithGroup = EnumValueDefinition & { data?: { group?: string } }

export function normalizeEnumCondition(
  op: string,
  value: unknown,
  enumValues?: EnumValueWithGroup[],
): { op: string; value: unknown } {
  if (!enumValues?.length) {
    return { op, value }
  }

  // 1. label-or-code → code(s), case-insensitive
  const raw = Array.isArray(value)
    ? (value as unknown[]).map(String)
    : [String(value)]
  const codes = raw.flatMap((entry) => {
    const lower = entry.toLowerCase()
    const matched = enumValues
      .filter(
        (e) =>
          e.value.toLowerCase() === lower || e.title.toLowerCase() === lower,
      )
      .map((e) => e.value)
    if (matched.length > 0) {
      return matched
    }
    // Fall back to matching a PISA status-group NAME (e.g. "in progress",
    // "in review") to every member code — a common natural-language phrasing
    // that names the group rather than a single status. Schema-derived, so it
    // stays correct as instances change their enums. A term that is neither a
    // value, a title, nor a group name (e.g. the semantic "open") falls through
    // as raw and is rejected loud by the caller.
    const groupCodes = enumValues
      .filter((e) => e.data?.group?.toLowerCase() === lower)
      .map((e) => e.value)
    return groupCodes.length > 0 ? groupCodes : [entry]
  })

  // 2. flip op when titles are duplicated (one label → many codes)
  const normalizedOp = normalizeOperatorForEnumValues(op, enumValues)

  // 3. expand each code to all sibling codes sharing its title.
  // expandEnumValuesWithSameTitle's return is inferred `unknown` only because of
  // its no-enumValues early return; that path is unreachable here (enumValues is
  // non-empty, guarded by the caller) and `codes` is already string[], so the
  // result is always string[].
  const expanded = expandEnumValuesWithSameTitle(codes, enumValues) as string[]

  // 4. Further expand across PISA data.group boundaries — covers cross-title
  //    groupings like Activity status where "Planning / Open" (appointments/calls)
  //    and "Not started" (tasks) share the "in progress" group but have different
  //    titles, so step 3's title-only expansion misses them. Collect all groups
  //    already in the expanded set, then add every code in those groups.
  const expandedSet = new Set(expanded)
  const groups = new Set<string>()
  for (const code of expanded) {
    const g = enumValues.find((e) => e.value === code)?.data?.group
    if (g) {
      groups.add(g)
    }
  }
  if (groups.size > 0) {
    for (const e of enumValues) {
      if (e.data?.group && groups.has(e.data.group)) {
        expandedSet.add(e.value)
      }
    }
  }
  const fullyExpanded = [...expandedSet]

  // Re-derive operator: group expansion may have produced multiple values even
  // when the original op was scalar (eq/neq). Flip to in/nin when needed.
  const finalOp =
    fullyExpanded.length > 1 && !isMultiValueOperator(normalizedOp)
      ? normalizedOp === 'neq'
        ? 'nin'
        : 'in'
      : normalizedOp

  return {
    op: finalOp,
    value: isMultiValueOperator(finalOp)
      ? fullyExpanded
      : (fullyExpanded[0] ?? null),
  }
}

/**
 * Shapes a non-enum condition's value to the operator, byte-identical to the
 * manual FilterBuilder's private `normalizeValueForOperator`
 * ([useFilterBuilder.ts](src/Atomic/Organisms/FilterBuilder/useFilterBuilder.ts)):
 * `in`/`nin` always get an array (a lone reference/relation id becomes `[id]`,
 * blanks become `[]`); `eq`/`neq` collapse an accidental array to its first
 * element. Enum conditions are handled by `normalizeEnumCondition` instead, which
 * already emits the correct array/scalar shape.
 */
export function normalizeValueForOperator(op: string, value: unknown): unknown {
  if (isMultiValueOperator(op)) {
    if (Array.isArray(value)) {
      return value
    }
    if (value === null || value === undefined || value === '') {
      return []
    }
    return [value]
  }
  if ((op === 'eq' || op === 'neq') && Array.isArray(value)) {
    return value[0] ?? null
  }
  return value
}

/**
 * Scans every field carrying enum values in the schema for one that recognizes
 * `value` (by code or display title, case-insensitive), excluding the field
 * that already failed. Pure reference fields (no enumValues) are skipped. Used
 * to turn a bare "not a valid status" error into an
 * actionable hint — e.g. "Lead" isn't a status but IS a group value, so the
 * user likely meant `group`. Returns the matching field keys with their titles.
 */
export function findFieldsForEnumValue(
  schemaAttributes: AdapterSchemaAttributes | undefined,
  value: string,
  excludeField: string,
): { field: string; title: string }[] {
  if (!schemaAttributes) {
    return []
  }
  const lower = value.toLowerCase()
  const matches: { field: string; title: string }[] = []
  for (const [field, attr] of Object.entries(schemaAttributes)) {
    if (field === excludeField || !attr?.enumValues?.length) {
      continue
    }
    const hit = attr.enumValues.some(
      (e) => e.value.toLowerCase() === lower || e.title.toLowerCase() === lower,
    )
    if (hit) {
      matches.push({ field, title: attr.title })
    }
  }
  return matches
}

/**
 * Builds the display-label maps the filter preview card renders from: field key →
 * localized title, and field key → { PISA code → label }.
 *
 * Derived from the schema, never from tool arguments. These maps are large (a
 * status enum has dozens of codes) and a model asked to copy one between tool
 * calls reliably drops entries — the card then falls back to printing raw codes
 * ("PSA_ORG_HDP_WRK, ..., active") instead of the single deduplicated label.
 */
export function buildFilterLabelMaps(
  schemaAttributes: AdapterSchemaAttributes | undefined,
  structure: SavedFilterGroup[],
): {
  fieldLabels: Record<string, string>
  enumLabels: Record<string, Record<string, string>>
} {
  const fieldLabels: Record<string, string> = {}
  const enumLabels: Record<string, Record<string, string>> = {}
  if (!schemaAttributes) {
    return { fieldLabels, enumLabels }
  }
  for (const group of structure) {
    for (const cond of group.and) {
      const attr = schemaAttributes[cond.field]
      if (!attr) {
        continue
      }
      fieldLabels[cond.field] = attr.title
      if (attr.enumValues?.length) {
        enumLabels[cond.field] = Object.fromEntries(
          attr.enumValues.map((e) => [e.value, e.title]),
        )
      }
    }
  }
  return { fieldLabels, enumLabels }
}

/** Resolves reference ids to their display names via `getReferenceDisplayValue`. */
async function resolveIdLabels(
  ids: string[],
  referenceTo: string,
  referenceFilter?: AdapterReferenceFilter,
): Promise<Record<string, string>> {
  if (ids.length === 0) {
    return {}
  }
  const { data } = getHostRuntime()
  return data.resolveReferenceDisplayValues(ids, referenceTo, referenceFilter)
}

/**
 * Resolves readable field + value labels for the relation/reference conditions
 * the enum-only `buildFilterLabelMaps` can't handle: relation-record selects
 * (`companies.id`), m:1 reference records (`accountManager`), and nested reference
 * fields (`accountManager.category`). Record ids become display names via
 * `getReferenceDisplayValue` (same source as the manual builder's
 * `resolveReferenceLabel`); the returned `valueLabels` merge into the preview
 * card's `enumLabels`, which already maps a raw value to its label. Async because
 * display names are live data and nested fields need the referenced schema.
 */
export async function buildRelationReferenceLabels(
  schemaAttributes: AdapterSchemaAttributes | undefined,
  relations: AdapterSchemaRelation[],
  structure: SavedFilterGroup[],
): Promise<{
  fieldLabels: Record<string, string>
  valueLabels: Record<string, Record<string, string>>
}> {
  const fieldLabels: Record<string, string> = {}
  const valueLabels: Record<string, Record<string, string>> = {}
  if (!schemaAttributes) {
    return { fieldLabels, valueLabels }
  }

  const { data } = getHostRuntime()
  const relationByPath = new Map(
    relations.map((rel) => [rel.relationPath, rel]),
  )

  for (const group of structure) {
    for (const cond of group.and) {
      const dot = cond.field.indexOf('.')
      const prefix = dot === -1 ? '' : cond.field.slice(0, dot)
      const suffix = dot === -1 ? '' : cond.field.slice(dot + 1)
      const ids = (
        Array.isArray(cond.value) ? cond.value : [cond.value]
      ).filter((v): v is string => typeof v === 'string' && v.length > 0)

      // Relation-record select: "<relationPath>.id"
      const relation = suffix === 'id' ? relationByPath.get(prefix) : undefined
      if (relation) {
        const targetClass =
          relation.entityName.charAt(0).toUpperCase() +
          relation.entityName.slice(1)
        fieldLabels[cond.field] = getLocalizedTitle(relation, targetClass)
        // Merge, never replace: the same field can appear in several OR groups
        // (e.g. "accountManager in [A]" OR "accountManager in [B]"), each carrying
        // only its own ids. Assigning would drop every earlier group's labels and
        // leak those ids raw on the card.
        valueLabels[cond.field] = {
          ...valueLabels[cond.field],
          ...(await resolveIdLabels(ids, targetClass)),
        }
        continue
      }

      // Nested relation-attribute: "<relationPath>.<field>" (e.g. "companies.group").
      // The relation path is not a scalar attribute, so its leaf is governed by the
      // RELATED entity's schema — mirrors build_filter's relationTargets resolution.
      // Without this the card leaks the raw dotted key and the raw enum code
      // (e.g. "companies.group is one of PSA_ORG_CST…"). Enum leaves become
      // value→title maps; reference leaves resolve ids to display names.
      const nestedRelation =
        suffix && suffix !== 'id' ? relationByPath.get(prefix) : undefined
      if (nestedRelation) {
        try {
          const targetClass =
            nestedRelation.entityName.charAt(0).toUpperCase() +
            nestedRelation.entityName.slice(1)
          const { schemaAttributes: nested } = await data.getSchema(
            data.getSchemaName(targetClass),
          )
          const nestedAttr = nested?.[suffix]
          if (nestedAttr) {
            fieldLabels[cond.field] =
              `${getLocalizedTitle(nestedRelation, targetClass)} / ${nestedAttr.title}`
            if (nestedAttr.enumValues?.length) {
              valueLabels[cond.field] = Object.fromEntries(
                nestedAttr.enumValues.map((e) => [e.value, e.title]),
              )
            } else if (
              nestedAttr.type === 'reference' &&
              nestedAttr.referenceTo
            ) {
              // Merge (see relation-record branch): accumulate every OR group's
              // id → name rather than overwrite.
              valueLabels[cond.field] = {
                ...valueLabels[cond.field],
                ...(await resolveIdLabels(
                  ids,
                  nestedAttr.referenceTo,
                  nestedAttr.referenceFilter,
                )),
              }
            }
          }
        } catch {
          // Unresolved relation target schema — the card falls back to the raw key.
        }
        continue
      }

      // m:1 reference record: "<refField>" (reference attr, record ids as value)
      if (dot === -1) {
        const attr = schemaAttributes[cond.field]
        if (
          attr?.type === 'reference' &&
          !attr.enumValues?.length &&
          attr.referenceTo
        ) {
          fieldLabels[cond.field] = attr.title
          // Merge (see relation-record branch): a reference field repeated across
          // OR groups must accumulate every group's id → name, not overwrite.
          valueLabels[cond.field] = {
            ...valueLabels[cond.field],
            ...(await resolveIdLabels(
              ids,
              attr.referenceTo,
              attr.referenceFilter,
            )),
          }
        }
        continue
      }

      // Nested reference field: "<refField>.<field>"
      const refAttr = schemaAttributes[prefix]
      if (refAttr?.type !== 'reference' || !refAttr.referenceTo) {
        continue
      }
      try {
        // Load the reference's declared target (Contact), not the concrete
        // subtype — the same class build_filter validates the leaf against, so the
        // card label describes the field that is actually filtered.
        const targetClass = refAttr.referenceTo
        const { schemaAttributes: nested } = await data.getSchema(
          data.getSchemaName(targetClass),
        )
        const nestedAttr = nested?.[suffix]
        if (!nestedAttr) {
          continue
        }
        fieldLabels[cond.field] = `${refAttr.title} / ${nestedAttr.title}`
        if (nestedAttr.enumValues?.length) {
          valueLabels[cond.field] = Object.fromEntries(
            nestedAttr.enumValues.map((e) => [e.value, e.title]),
          )
        } else if (nestedAttr.type === 'reference' && nestedAttr.referenceTo) {
          // Merge (see relation-record branch): a nested reference repeated across
          // OR groups must accumulate every group's id → name, not overwrite.
          valueLabels[cond.field] = {
            ...valueLabels[cond.field],
            ...(await resolveIdLabels(
              ids,
              nestedAttr.referenceTo,
              nestedAttr.referenceFilter,
            )),
          }
        }
      } catch {
        // Unresolved nested schema — the card falls back to the raw dotted key.
      }
    }
  }

  return { fieldLabels, valueLabels }
}

/**
 * Merges multiple `in`/`nin` conditions on the same field within one AND group
 * into a single condition with a unioned value array. The model sometimes emits
 * one condition per value (`group in [CST]` AND `group in [LEA]`), which is a
 * logical contradiction for a single-valued field — PISA collides on the
 * duplicate key and returns 422. "is one of" is expressly for this: both codes
 * belong in one condition (`group in [CST, LEA]`). Non-`in`/`nin` conditions and
 * distinct fields are left untouched and keep their original order.
 */
export function mergeSameFieldConditions(
  conditions: SavedFilterCondition[],
): SavedFilterCondition[] {
  const merged: SavedFilterCondition[] = []
  // field+op → index in `merged` of the condition accumulating that field's values
  const indexByKey = new Map<string, number>()

  for (const cond of conditions) {
    if (cond.op !== 'in' && cond.op !== 'nin') {
      merged.push(cond)
      continue
    }
    const key = `${cond.field}::${cond.op}`
    const values = Array.isArray(cond.value) ? cond.value : [cond.value]
    const existingIndex = indexByKey.get(key)
    if (existingIndex === undefined) {
      indexByKey.set(key, merged.length)
      merged.push({ ...cond, value: [...values] })
    } else {
      const target = merged[existingIndex]
      const targetValues = Array.isArray(target?.value) ? target.value : []
      // in/nin values are primitive enum codes / reference IDs — dedupe, keep order
      merged[existingIndex] = {
        field: cond.field,
        op: cond.op,
        value: [...new Set([...targetValues, ...values])],
      }
    }
  }

  return merged
}

/** field+op key used to order conditions and groups deterministically. */
function conditionKey(cond: SavedFilterCondition | undefined): string {
  return cond ? `${cond.field}::${cond.op}` : ''
}

/** Wraps a scalar in a single-element array and sorts, for canonical comparison. */
function toSortedValueArray(value: unknown): unknown[] {
  const arr = Array.isArray(value)
    ? value
    : value === null || value === undefined || value === ''
      ? []
      : [value]
  return [...arr].sort((a, b) => String(a).localeCompare(String(b)))
}

/**
 * Unifies the two equivalent representations of a membership condition that the
 * manual FilterBuilder and the bot's build_filter emit for the SAME logical filter:
 * the manual builder stores a single value as `eq X` (scalar), while build_filter
 * stores it as `in [X]` (array). For equality comparison they must collapse to one
 * shape, so `eq`/`in` → `in` and `neq`/`nin` → `nin`, with the value always a sorted
 * array. Every other operator (gt, lt, contains, …) is left untouched. Comparison
 * only — stored filters are never rewritten.
 */
function canonicalizeOpValue(
  op: string,
  value: unknown,
): { op: string; value: unknown } {
  if (op === 'eq' || op === 'in') {
    return { op: 'in', value: toSortedValueArray(value) }
  }
  if (op === 'neq' || op === 'nin') {
    return { op: 'nin', value: toSortedValueArray(value) }
  }
  return { op, value }
}

/**
 * Produces an order-independent canonical form of a filter for equality
 * comparison — the basis for identical-filter detection across segments
 * (find_matching_segment). The same-field merges (mergeSameFieldConditions/
 * mergeSameFieldGroups) dedupe values but do NOT sort, so two logically-equal
 * filters built in a different order still differ under JSON.stringify. On top of
 * those merges this:
 *   (a) unifies equivalent membership operators (`eq X` ≡ `in [X]`, `neq X` ≡
 *       `nin [X]`) to one shape with a sorted value array, so a manually-built
 *       filter (single value → `eq`) matches the bot's (single value → `in`),
 *   (b) sorts conditions within each group by field then op,
 *   (c) sorts groups by their full (already condition-sorted) serialized content,
 *       so two groups sharing a first condition but differing later still order
 *       deterministically.
 * OR (groups) and AND (conditions) are both commutative, so reordering preserves
 * meaning. Compare two filters via
 * `JSON.stringify(canonicalizeFilter(a)) === JSON.stringify(canonicalizeFilter(b))`.
 */
export function canonicalizeFilter(
  structure: SavedFilterStructure,
): SavedFilterStructure {
  const merged = mergeSameFieldGroups(
    structure.or.map((group) => ({
      and: mergeSameFieldConditions(group.and),
    })),
  )

  const canonicalGroups = merged.map((group) => ({
    and: group.and
      .map((cond) => {
        const { op, value } = canonicalizeOpValue(cond.op, cond.value)
        return { ...cond, op, value }
      })
      .sort((a, b) => conditionKey(a).localeCompare(conditionKey(b))),
  }))

  // Conditions within each group are already sorted above, so the serialized
  // group is itself a complete canonical key — order groups by it.
  canonicalGroups.sort((a, b) =>
    JSON.stringify(a).localeCompare(JSON.stringify(b)),
  )

  return { or: canonicalGroups }
}

/**
 * Collapses multiple single-condition OR groups that filter the same field with
 * the same `in`/`nin` op into one group with a unioned value array. The model
 * often emits `(group in [CST]) OR (group in [LEA])` — logically identical to
 * `group in [CST, LEA]` but not the canonical shape the manual builder produces.
 * Only single-condition groups are eligible; multi-condition AND groups (which
 * express genuinely distinct OR branches) pass through untouched and keep order.
 */
export function mergeSameFieldGroups(
  groups: SavedFilterGroup[],
): SavedFilterGroup[] {
  const merged: SavedFilterGroup[] = []
  // field+op → index in `merged` of the single-condition group accumulating values
  const indexByKey = new Map<string, number>()

  for (const group of groups) {
    const only = group.and.length === 1 ? group.and[0] : undefined
    if (!only || (only.op !== 'in' && only.op !== 'nin')) {
      merged.push(group)
      continue
    }
    const key = `${only.field}::${only.op}`
    const values = Array.isArray(only.value) ? only.value : [only.value]
    const existingIndex = indexByKey.get(key)
    if (existingIndex === undefined) {
      indexByKey.set(key, merged.length)
      merged.push({ and: [{ ...only, value: [...values] }] })
    } else {
      const target = merged[existingIndex]?.and[0]
      const targetValues = Array.isArray(target?.value) ? target.value : []
      merged[existingIndex] = {
        and: [
          {
            field: only.field,
            op: only.op,
            value: [...new Set([...targetValues, ...values])],
          },
        ],
      }
    }
  }

  return merged
}

/**
 * AND-combines a freshly-built delta filter into an existing stored filter, so
 * refining ("add active ones") NARROWS the base instead of replacing it. Existing
 * conditions are taken verbatim from `base` — the model never re-types them, so it
 * cannot silently drop one (the class of bug this fixes). Semantics mirror the manual
 * builder: each existing OR branch is additionally required to satisfy the new
 * conditions, i.e. the AND distributes over the OR (`(A OR B)` + `C` →
 * `(A AND C) OR (B AND C)`). Same-field values collapse via the shared merge helpers.
 * An empty side is a no-op.
 */
export function andCombineFilters(
  base: SavedFilterGroup[],
  delta: SavedFilterGroup[],
): SavedFilterGroup[] {
  if (base.length === 0) {
    return delta
  }
  if (delta.length === 0) {
    return base
  }
  const combined = base.flatMap((baseGroup) =>
    delta.map((deltaGroup) => ({
      and: mergeSameFieldConditions([...baseGroup.and, ...deltaGroup.and]),
    })),
  )
  return mergeSameFieldGroups(combined)
}
